import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, HostBinding, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Agent, Company, CompanyService, CustomerFormMetadata, EventLogCreateDTO, EventName, EventService, FormEventName, FormView, FormViewService, IClient, IFormEventsData, IndexSummary, type IThemeData, PosthogService, Submission } from '@xilo-mono/form-contracts';
import { CustomerFormFacade, FormViewerComponent } from '@xilo-mono/form-viewer';
import { cloneDeep, findIndex, isNumber } from 'lodash';
import { catchError, defaultIfEmpty, EMPTY, first, forkJoin, Observable, of, ReplaySubject, takeUntil, takeWhile, tap } from 'rxjs';
import { applyTheme, FormThemeService } from '../../../../common/form-theme.service';
import { SummaryItem } from '../../models';
import { SEOService } from '../../services';
import { LoggingService } from '../../services/log.service';
import { PageAnimationStateTypes } from './page.interface';

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  animations: [
    trigger('PageAnimation', [
      state('unloaded', style({ opacity: 0, transform: 'translateY(120px)' })),
      state('loaded', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('unloaded => loaded', animate('500ms ease-out'))
    ]),
    trigger('SummaryAnimation', [
      state('none', style({ opacity: 0, transform: 'translateY(20px)' })),
      state('loaded', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('none => loaded', animate('250ms ease-out'))
    ])
  ]
})
export class PageComponent implements OnInit, OnDestroy {
  @ViewChild(FormViewerComponent) formViewer: FormViewerComponent;

  private readonly formThemeService = inject(FormThemeService);
  queryParams: Params = Object.assign({}, this.route.snapshot.queryParams);
  params: Params = Object.assign({}, this.route.snapshot.params);
  loaded = false;
  showLoader = true;
  currentPageNumber: number;
  pageName = this.params.page;
  width = window.innerWidth;
  company: Company;
  client: IClient;
  submission: Submission;
  pageAnimationState: PageAnimationStateTypes = 'loaded';
  summaryAnimationState: 'loaded' | 'none' = 'none';
  formModel = {};
  sectionIndex: number;
  questionIndex: number;
  currentQuestionNumber: number;
  subsectionIndex: number;
  formOptions = {
    formState: {
      questionnum: 0,
      sectionnum: 0,
      subsection: 0
    }
  };
  form = new FormGroup({});
  fields: FormlyFieldConfig[] = [];
  formView: FormView;
  firstQuestionLoaded = false;
  formCompleted = false;
  animationInProgress$: Observable<boolean>;
  metadata: CustomerFormMetadata;
  isSubmitting = false;
  disableNextBtn = false;
  submissionLoaded = false;
  hasRater: boolean;

  mobileSummaryOpened = false;
  quotesFeatureFlag = false;
  footerText: string;

  get token() {
    return localStorage.getItem('token');
  }

  get tokenIsActive() {
    return this.token && this.token !== 'inactive';
  }

  get isMobile() {
    return this.width <= 767;
  }

  get showBanner() {
    if (typeof this.formView?.metadata?.showBanner !== 'undefined') {
      return this.formView?.metadata?.showBanner;
    } else {
      return true;
    }
  }

  get showBannerOnMobile() {
    if (typeof this.formView?.metadata?.showBannerOnMobile !== 'undefined') {
      return this.formView?.metadata?.showBannerOnMobile;
    } else {
      return true;
    }
  }

  get phoneNumber() {
    if (this.formView?.metadata?.customContactInfo) {
      return this.formView?.metadata?.phoneNumber;
    } else {
      return this.company?.contactNumber;
    }
  }

  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  @HostBinding('class') className = 'container';

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: { innerWidth: number } }) {
    this.width = event.target.innerWidth;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private eventService: EventService,
    public formViewService: FormViewService,
    private customerFormFacade: CustomerFormFacade,
    private companyService: CompanyService,
    private seoService: SEOService,
    private posthogService: PosthogService,
    private logService: LoggingService,
  ) {
  }

  ngOnInit() {
    this.setAuthToken();
    this.params = Object.assign({}, this.route.snapshot.params);
    this.animationInProgress$ = this.customerFormFacade.animationInProgress$;
    this.formView = this.formViewService.formView;
    this.setMetadata();
    this.loadSubmission();
    this.loadForm();
    this.customerFormFacade.newCustomerSubmissionCreated$.pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.setComponentData(result.submission);
          setTimeout(() => {
            this.handleEvents('Visited XILO');
          }, 250)
        }
      });
    this.customerFormFacade.submissionLoaded$.pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.setComponentData(result.submission);
        }
      });
    this.formViewService.eventFired.pipe(takeUntil(this.destroyed$))
      .subscribe({
      next: ((event: FormEventName) => {
        if (event) this.handleEvents(event);
      }),
      error: (e: Error) => console.error(`Error in event fired subject: `, e)
    });
    this.customerFormFacade.loadState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((loadState) => {
        if (loadState.form && !this.loaded) {
          this.formLoaded();
        }
        if (loadState.form && loadState.submission && !this.submissionLoaded) {
          this.fields = this.formViewService.formView.components;
          this.setIndexes();
          this.subscribeToSectionsLoaded();

          this.submissionLoaded = true;
        }
      });
    this.customerFormFacade.submitState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((submitting) => {
        console.log('Submitting Subscription: ', submitting);
        this.isSubmitting = submitting;
      });
    this.companyService.companyDetails
      .pipe(
        first((company: Company) => (company?.id ? true : false)),
        takeUntil(this.destroyed$)
      ).subscribe((company: Company) => {
        this.company = company;
        this.quotesFeatureFlag = company?.features?.quotes;
        this.hasRater = company?.features?.rater;
        try {
          this.seoService
            .getSEO(this.company.id)
            .pipe(
              first((res) => (res.title ? true : false)),
              catchError(() => EMPTY), // If an error occurs, return an EMPTY observable
              takeUntil(this.destroyed$)
            )
            .subscribe();
        } catch (error) {
          console.log('###WARN: ', error);
        }
      });
  }

  setComponentData(submission: Submission) {
    if (!this.client) this.client = cloneDeep(submission?.client);
    if (!this.formView) this.formView = cloneDeep(submission.form);
    if (!this.company) this.company = cloneDeep(submission.company);
    if (!this.submission) this.submission = cloneDeep(submission);
  }

  setMetadata() {
    this.metadata = {
      companyId: this.queryParams.companyId,
      clientId: this.queryParams.clientId,
      agentId: this.queryParams.agentId,
      submissionId: this.queryParams.submissionId,
      referredBy: this.queryParams.ref,
      version: this.queryParams.version
    };
    // FIXME: Adding change after object to keep git history update on line 218
    // adding this change to confidently move forward without breakage
    this.customerFormFacade.setMetadata(this.metadata);
  }

  loadForm() {
    const { formId, version, companyId } = this.queryParams;
    let { submissionId } = this.queryParams;
    submissionId = this.tokenIsActive ? submissionId : null;
    this.customerFormFacade.loadForm(formId, companyId, version, submissionId);
  }

  private setAuthToken() {
    const { token } = this.queryParams;
    if (token && token !== 'undefined' && token !== 'null') {
      localStorage.setItem('token', token);
      delete this.queryParams.token;
      this.refreshRoute();
    }
  }

  private formLoaded() {
    this.fields = this.formViewService.formView.components;
    this.formView = this.formViewService.formView;
    const { metadata } = this.formView;
    const { version } = this.queryParams;
    if (metadata && metadata.version && version !== metadata.version) {
      this.queryParams.version = metadata.version;
      this.refreshRoute();
    }
    setTimeout(() => {
      this.loaded = true;
      this.pageAnimationState = 'loaded';
      this.summaryAnimationState = 'loaded';
      forkJoin([
        this.companyService.companyDetails.pipe(
          first((company: Company) => (company?.id ? true : false)),
          defaultIfEmpty(undefined),
          catchError(() => of(undefined)),
        ),
        this.formThemeService.formTheme$.pipe(first()),
      ]).pipe(
        tap(([company, formTheme]) => {
          if (company) {
            this.setBrandTheme(metadata, company, formTheme?.data);
          }
        }),
        takeUntil(this.destroyed$)
      ).subscribe();
      this.cdr.detectChanges();
    }, 10);
  }

  private setBrandTheme(metadata: any, company: Company, formThemeData: IThemeData | undefined) {
    const primaryBrandColor = formThemeData?.branding?.primaryColor || metadata.brandColor || company.brandColor || '#6e71ff';
    const secondaryBrandColor = formThemeData?.branding?.secondaryColor || metadata.brandSecondaryColor || company.brandSecondaryColor || '#23d6c8';

    const theme = {
      'brand-color': primaryBrandColor,
      'brand-color-light': primaryBrandColor + '33',
      'hover-color': formThemeData?.branding?.primaryColor || metadata.brandColor || company.brandColor || '#E5E5FB',
      'secondary-color': secondaryBrandColor,
      'secondary-color-light': secondaryBrandColor + '33'
    };
    this.footerText = metadata?.footerText || company?.data?.formFooterText || null;
    let themeVariablesModified = false;
    Object.keys(theme).forEach((k) => {
      if (theme[k]) {
        document.documentElement.style.setProperty(`--${k}`, theme[k]);
        // here we can unintentionally override Form Theme variables,
        // so if we do this - reapply form variables.
        themeVariablesModified = true;
      }
    });
    if (themeVariablesModified && formThemeData) {
      applyTheme(formThemeData);
    }
  }

  loadSubmission() {
    if (this.queryParams.submissionId && this.tokenIsActive) {
      this.customerFormFacade.loadSubmission(this.queryParams.submissionId);
    } else {
      this.resetSubmission();
      if (!this.queryParams.companyId) {
        this.queryParams = Object.assign({}, this.route.snapshot.queryParams);
      }
      const metadata = {
        companyId: this.queryParams.companyId,
        formId: this.queryParams.formId,
        agentId: this.queryParams.agentId,
        referredBy: this.queryParams.ref,
        version: this.queryParams.version,
        agent: this.queryParams.agent
      };
      this.customerFormFacade.newCustomerSubmission(metadata);
    }
  }

  resetSubmission() {
    delete this.queryParams.submissionId;
    delete this.queryParams.version;
    delete this.queryParams.clientId;
    this.refreshRoute();
    localStorage.setItem('token', 'inactive');
    localStorage.setItem('xiloEvents', '[]');
  }

  setIndexes() {
    this.questionIndex = +this.queryParams.question || 0;
    this.sectionIndex = +this.queryParams.section || 0;
    this.subsectionIndex = +this.queryParams.subsection || 0;
    this.queryParams.question = this.questionIndex;
    this.queryParams.section = this.sectionIndex;
    this.queryParams.subsection = this.subsectionIndex;
  }

  subscribeToSectionsLoaded() {
    const sections = this.fields[0].fieldGroup.length || 0;
    this.formViewService.sectionLoaded$
      .pipe(
        takeWhile((loaded) => loaded <= sections),
        takeUntil(this.destroyed$)
      )
      .subscribe((loaded) => {
        if (loaded === sections) {
          this.loadAnswers();
        }
      });
  }

  loadAnswers() {
    const responses = this.formViewService.submission?.responses;
    const submissionResponses = Array.isArray(responses)
      ? responses[0]
      : responses;
    if (submissionResponses) {
      this.formViewService.form.patchValue(submissionResponses);
      this.formViewService.model = submissionResponses;
      this.formModel = submissionResponses;
      const fields = cloneDeep(this.formViewService.fields);
      this.customerFormFacade.setFields(fields);
    }
    this.customerFormFacade.validateProgress({
      sectionIndex: this.sectionIndex,
      questionIndex: this.questionIndex,
      subsectionIndex: this.subsectionIndex
    });
    this.firstQuestionLoaded = true;
    this.showLoader = false;
    this.subscribeToIndexChanges();
    this.subscribeToMetadataChanges();
    this.subscribeToRepeatSectionActions();
  }

  subscribeToIndexChanges() {
    this.customerFormFacade.indexSummary$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ sectionIndex, questionIndex, subsectionIndex }) => {
        const indexesChanged = this.indexesChanged({
          sectionIndex,
          subsectionIndex,
          questionIndex
        });
        const triggerAnimation =
          this.sectionIndex !== sectionIndex ||
          this.subsectionIndex !== subsectionIndex;
        if (triggerAnimation) {
          this.triggerPageAnimation();
        }
        if (indexesChanged) {
          this.sectionIndex = sectionIndex;
          this.questionIndex = questionIndex;
          this.subsectionIndex = subsectionIndex;
          this.queryParams = {
            ...this.queryParams,
            section: sectionIndex,
            question: questionIndex,
            subsection: subsectionIndex
          };
          if (this.firstQuestionLoaded) {
            this.refreshRoute();
          }
          const questions = this.currentSectionQuestions();
          if (questions) {
            const currentQuestion = questions[questionIndex];
            const visibleQuestions = questions.filter((q) => !q.hide);
            this.currentQuestionNumber = visibleQuestions.findIndex(
              (q) => q.key === currentQuestion.key
            );
          }
        }
      });
  }

  indexesChanged(indexes: IndexSummary) {
    return (
      indexes.sectionIndex !== this.sectionIndex ||
      indexes.subsectionIndex !== this.subsectionIndex ||
      indexes.questionIndex !== this.questionIndex
    );
  }

  subscribeToMetadataChanges() {
    this.customerFormFacade.metadata$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((metadata) => {
        if (this.metadataChanged(metadata)) {
          this.metadata = metadata;
          const updatedQueryParams = {
            companyId: metadata.companyId,
            clientId: metadata.clientId,
            submissionId: metadata.submissionId,
            agentId: metadata.agentId,
            referredBy: metadata.referredBy
          };
          this.queryParams = {
            ...this.queryParams,
            ...updatedQueryParams
          };
          if (this.firstQuestionLoaded) {
            this.refreshRoute();
          }
        }
      });
  }

  metadataChanged(newMetadata: CustomerFormMetadata) {
    if (this.metadata) {
      return !Object.keys(this.metadata)
        .map((key) => this.metadata[key] === newMetadata[key])
        .reduce((a, b) => a && b, true);
    } else {
      return true;
    }
  }

  refreshRoute() {
    this.router.navigate([], {
      queryParams: {
        ...this.queryParams
      }
    });
  }

  subscribeToRepeatSectionActions() {
    this.customerFormFacade.addRepeatSection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.addRepeatSection();
      });

    this.customerFormFacade.removeRepeatSection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ sectionIndex, repeatIndex }) => {
        this.removeRepeatSection(sectionIndex, repeatIndex);
      });
  }

  triggerPageAnimation() {
    this.pageAnimationState = 'unloaded';
    setTimeout(() => {
      this.pageAnimationState = 'loaded';
    }, 50);
  }

  previous() {
    this.customerFormFacade.previous();
  }

  onNextForm() {
    this.disableNextBtn = true;
    const currentPage = this.fields[0].fieldGroup[this.sectionIndex];
    if (currentPage.formControl) currentPage.formControl.markAsTouched();
    const fireFormStarted = this.sectionIndex === 0 && !this.isLastQuestion();
    if (fireFormStarted) this.handleEvents('Started Form');
    if (this.isLastQuestion()) {
      this.isSubmitting = true;
      this.disableNextBtn = false;
      this.handleEvents('Finished Form');
      this.formViewer?.onSubmit();
    } else {
      this.disableNextBtn = false;
      this.customerFormFacade.next();
    }
  }

  // TODO : Handle errors on all potential calls
  handleEvents(eventName: FormEventName) {
    try {
      const events: EventName[] = JSON.parse(localStorage.getItem('xiloEvents') || '[]') as EventName[] || [];
      // if (events.includes(formEvent.name)) return console.log(`Event {${formEvent.name}} already logged`);
      if (events.includes(eventName)) return;
      events.push(eventName);
      localStorage.setItem('xiloEvents', JSON.stringify(events));
      const clientId = this.client.id ? Number(this.client.id) : null;
      const companyId = this.company.id ? Number(this.company.id) : null;
      const formViewId = this.formView.id ? Number(this.formView.id) : null;
      console.log('Client Agent ID: ', this.client.clientAgentId);
      let agentId = null;
      if (this.client.clientAgentId) agentId = this.client.clientAgentId;
      else if (this.queryParams.agentId) {
        agentId = Number(this.queryParams.agentId);
        this.client.clientAgentId = agentId;
      }
      console.log('Agent ID: ', agentId);
      const formKey = this.formView.metadata.key;
      const userTable = agentId ? 'agent' : null;
      const source =
        this.client.referredBy ||
        this.formViewService.submission.metadata.referredBy ||
        this.queryParams.referredBy ||
        this.queryParams.ref ||
        this.formView.title;
      const eventData = {
        name: eventName,
        formId: formViewId,
        formKey,
        companyId,
        userId: agentId ? agentId : null,
        userType: userTable,
        clientId,
        source: source,
        data: {},
        companyName: this.company?.name ?? '',
        formTitle: this.formView?.title ?? '',
      };
      this.fireEvent(eventData, 'ok');
    } catch (error) {
      console.log(`Handle Events Failed ${eventName} Error: %o`, error);
      this.logService.critical(`Handle Events Failed ${eventName}`, { event: eventName, queryParams: this.queryParams, error })
    }
  }

  onFormCompleted() {
    this.formCompleted = true;
    const params = {
      formId: this.queryParams.formId,
      companyId: this.queryParams.companyId,
      userId: this.client?.id,
      version: this.queryParams.version,
      submissionId: this.queryParams.submissionId,
      clientId: this.queryParams.clientId,
      formType: 'customer-form',
      referredBy: this.queryParams.ref,
      tasklistId: this.queryParams.tasklistId,
      originalSubId: this.queryParams.originalSubId
    };
    if (this.hasRater && this.formView?.metadata?.triggerRates) {
      this.router.navigate(['form', 'rates'], { queryParams: params });
    } else {
      this.handleEvents('New Lead');
      this.router.navigate(['form', 'thank-you'], { queryParams: params });
    }
  }

  agentAssigned(agent: Agent) {
    try {
      console.log(agent);
      this.client.clientAgentId = Number(agent.id);
      this.handleEvents('Agent Assigned');
    } catch (e) {
      console.log(e);
      this.logService.critical(`Assign Agent Failed for Agent ${agent.id}`, { queryParams: this.queryParams, error: e })
    }
  }

  currentSection() {
    if (this.fields[0]?.fieldGroup && isNumber(+this.sectionIndex)) {
      return this.fields[0].fieldGroup[this.sectionIndex];
    } else {
      return null;
    }
  }

  currentSectionQuestions() {
    const currentSection = this.currentSection();
    if (currentSection) {
      return currentSection.fieldArray
        ? currentSection.fieldArray?.fieldGroup
        : currentSection.fieldGroup;
    } else {
      return [];
    }
  }

  currentQuestion() {
    const questions = this.currentSectionQuestions();
    if (questions) {
      return questions[this.questionIndex];
    } else {
      return null;
    }
  }

  currentVisibleSectionQuestions() {
    return this.currentSectionQuestions().filter((q) => !q.hide);
  }

  currentQuestionValid() {
    const currentSection = this.currentSection();
    const currentQuestion = this.currentQuestion();
    if (currentSection && currentQuestion) {
      if (this.isRepeatableSection()) {
        const subsection = (currentSection.formControl as FormGroup)?.controls[
          this.subsectionIndex
          ] as FormGroup;
        if (subsection) {
          const question = subsection.controls[currentQuestion.key as string];
          return question?.valid;
        }
      } else {
        const question = (currentSection.formControl as FormGroup)?.controls[
          currentQuestion.key as string
          ];
        return question?.valid;
      }
    }
    return false;
  }

  currentSectionValid() {
    const currentSection = this.currentSection();
    if (currentSection) {
      return currentSection.formControl?.valid;
    }
    return false;
  }

  currentSectionQuestionCount() {
    const currentSection = this.currentSection();
    let count = 0;
    if (currentSection) {
      const fieldGroup = currentSection?.fieldArray
        ? currentSection?.fieldArray.fieldGroup
        : currentSection?.fieldGroup;

      if (fieldGroup) {
        const fieldGroupAndIndex = this.visibleFieldGroupLengthAndIndex(
          fieldGroup,
          this.questionIndex
        );
        count = fieldGroupAndIndex.fieldLength;
      }
      return count;
    }
  }

  questionValid(questionIndex: number) {
    const sectionKey = this.currentSection().key as string;
    const question = this.currentSectionQuestions()[questionIndex];
    const questionKey = question?.key as string;
    if (questionKey && sectionKey) {
      const control = this.formViewService.getControl(
        sectionKey,
        questionKey,
        this.subsectionIndex
      );
      return control?.valid;
    } else {
      return false;
    }
  }

  previousButtonEnabled() {
    return (
      this.questionIndex > 0 ||
      this.sectionIndex > 0 ||
      this.subsectionIndex > 0
    );
  }

  nextButtonEnabled() {
    if (this.isLastQuestion()) {
      return this.form.valid;
    }
    if (this.isLastQuestionInSection()) {
      return this.currentSectionValid();
    } else {
      return this.currentQuestionValid();
    }
  }

  currentSectionTemplateOptions() {
    const currentSection = this.currentSection();
    if (currentSection) {
      return currentSection.fieldArray
        ? currentSection.fieldArray?.templateOptions
        : currentSection.templateOptions;
    }
    return null;
  }

  isLastQuestionInSection() {
    const visibleQuestions = this.currentVisibleSectionQuestions();
    const currentQuestion = this.currentQuestion();
    const index = findIndex(
      visibleQuestions,
      (q) => q.key === currentQuestion.key
    );
    return index === visibleQuestions.length - 1;
  }

  onAlert(obj: { type: string; message: string }) {
    if (obj.type === 'success') {
      // this.toastService.showSuccess(obj.message);
    }
  }

  isLastQuestion() {
    if (this.fields?.length && this.fields[0].fieldGroup) {
      const fieldGroupAndIndex = this.visibleFieldGroupLengthAndIndex(
        this.fields[0].fieldGroup,
        this.sectionIndex || 0
      );
      return (
        fieldGroupAndIndex.index === fieldGroupAndIndex.fieldLength - 1 &&
        this.isLastQuestionInSection()
      );
    } else {
      return false;
    }
  }

  visibleFieldGroupLengthAndIndex(fieldGroup: any, index: any) {
    let fields = cloneDeep(fieldGroup);
    if (fields.some((field) => field.hide)) {
      fields = fields.filter((field) => !field.hide);
      const visibleFieldGroup = fieldGroup[index];
      const currentKey = visibleFieldGroup.key;
      index = fields.findIndex((field) => field.key === currentKey);
    }
    return {
      fieldLength: fields.length,
      index
    };
  }

  showAddAnother() {
    return this.isLastQuestionInSection() && this.isRepeatableSection();
  }

  isRepeatableSection() {
    const templateOptions = this.currentSectionTemplateOptions();
    if (templateOptions) {
      return templateOptions.hasRepeatButtons;
    } else {
      return false;
    }
  }

  addRepeatSection() {
    const currentSection = this.currentSection();
    if (currentSection) {
      currentSection.templateOptions.onAdd();
      this.customerFormFacade.addRepeatSection(currentSection.key as string);
    }
  }

  removeRepeatSection(sectionIndex, repeatIndex) {
    const section = this.fields[0].fieldGroup[sectionIndex];
    if (section) {
      section.templateOptions.onRemove(repeatIndex);
      this.customerFormFacade.removeRepeatSection(sectionIndex, repeatIndex);
    }
  }

  handleDeleteMultipleItemTrigger(summaryItem: SummaryItem) {
    // add delete logic here
  }

  toggleMobileSummary() {
    this.mobileSummaryOpened = !this.mobileSummaryOpened;
  }

  fireEvent(formEvent: IFormEventsData, status: 'ok' | 'fail', error?: string) {
    if (!formEvent.companyId) {
      console.log(`${formEvent.name} fired but companyId was not set`);
      this.logService.critical(`${formEvent.name} fired but companyId was not set`, { event: formEvent.name, queryParams: this.queryParams })
    }
    const xiloEventNameMap: { [key: FormEventName]: EventName } = {
      'Agent Assigned': 'client:agent_assigned',
      'Visited XILO': 'form_viewer:application_viewed',
      'Started Form': 'form_viewer:application_started',
      'New Lead': 'form_viewer:application_new_lead',
      'Finished Form': 'form_viewer:application_completed',
    };
    const xiloEvent: EventLogCreateDTO = {
      name: xiloEventNameMap[formEvent.name],
      companyId: formEvent.companyId,
      formKey: formEvent.formKey,
      formId: formEvent.formId,
      result: status,
      userId: formEvent.userId,
      userTable: formEvent.userType,
      source: formEvent.source,
      objId: formEvent.clientId.toString(),
      clientId: formEvent.clientId,
      ip: '',
      occurredAt: new Date(),
      data: {
        formType: 'customer-form',
      }
    };
    try {
      this.eventService.logEvent(xiloEvent);
      this.posthogService.captureFormEvent(formEvent);
    } catch (error) {
      console.log('Error logging event: ', error);
      this.logService.critical(`Error logging event: ${formEvent.name}`, { event: formEvent.name, queryParams: this.queryParams, error })
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  mobileQuestionSummary() {
    const questions = this.currentSectionQuestions();
    if (questions) {
      const visibleQuestions = questions.filter((q) => !q.hide);
      return visibleQuestions.map((q, index) => {
        const valid = this.questionValid(index);
        return {
          progress: index <= this.questionIndex,
          attended: index > this.questionIndex && valid,
          error: !valid
        };
      });
    }
  }

  getStyle() {
    if (this.isMobile) {
      return {
        'padding-bottom': '50px'
      };
    } else if (!this.showBanner && !this.isMobile) {
      return {
        height: 'calc(100vh - 31px)',
        'padding-bottom': '100px'
      };
    } else {
      return {
        'padding-bottom': '100px'
      };
    }
  }
}
